/**
 * Rogue kit identity (plan 29 F9, lane BY 2026-10-11).
 *
 * Authored looks for the reachable rogue abilities that resolved to a school
 * default or shared a look with a kit-mate: Backstab, Eviscerate and
 * Assassinate were all the generic smear with the hit_physical / hit_critical
 * flash; Shadow Step drew Blink's purple blob; Vanish and Cloak of Shadows
 * had no visible cast of their own; Fan of Knives and Lucky Seven landed as
 * the red school puff. Each def is named by the ability's TOML
 * (`cast_effect` / `impact_effect`), so the local module, the confirmed
 * impact and a remote replay all read the same id.
 *
 * Colours follow each ability's TOML `aura_color`: Backstab magenta,
 * Eviscerate blood red, Assassinate deep crimson, Cloak of Shadows night
 * violet, Fan of Knives poison green, Vanish smoke, Lucky Seven gold, Shadow
 * Step indigo. Bursts end (one-shot tweens); no screen shake or flash (plan
 * 29 F8 — the hit tier owns it).
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, slashArc, shardBurst, glintScatter, groundCloud, wispRise, starFlash } from './burstBuilders';

const MAGENTA = 0xd040a0;
const MAGENTA_HOT = 0xffb0e0;
const BLOOD = 0xd01830;
const BLOOD_DEEP = 0x7a0010;
const CRIMSON = 0x8a0020;
const CRIMSON_EDGE = 0xff6070;
const NIGHT = 0x3c1a6e;
const NIGHT_EDGE = 0x9a70e0;
const POISON = 0x7ccf5a;
const STEEL = 0xe0e4ea;
const SMOKE = 0x8a8aa8;
const SMOKE_DARK = 0x4a4a62;
const GOLD = 0xffd700;
const GOLD_HOT = 0xfff2a0;
const INDIGO = 0x4a3cc8;
const INDIGO_EDGE = 0xa8a0ff;

/** Backstab's hit: a magenta puncture — a sharp four-point flare and a short thrust line. */
const BACKSTAB_PUNCTURE: VFXEffectConfig = {
  id: 'backstab_puncture',
  name: 'Backstab Puncture',
  category: 'burst',
  description: 'A sharp magenta four-point flare where the dagger goes in, a short thrust line and hot glints',
  primitives: [
    starFlash({ points: 4, innerRadius: 4, outerRadius: 20, color: MAGENTA_HOT, glow: true, spinRad: Math.PI * 0.25, expandTo: 1.5, durationMs: 220 }),
    { type: 'rect', width: 34, height: 3, color: MAGENTA, alpha: 0.95, depth: 96, offsetX: -10,
      tween: { x: 14, alpha: 0, duration: 160, ease: 'Power3' } },
    ringBlast({ radius: 9, color: MAGENTA, alpha: 0.55, stroke: { color: MAGENTA_HOT, width: 2 }, expandTo: 2.2, durationMs: 260 }),
    glintScatter({ count: 3, spread: 16, color: MAGENTA_HOT, durationMs: 200, delayMs: 60 }),
  ],
};

/** Eviscerate's hit: two crossing blood-red cuts, then droplets falling. */
const EVISCERATE_REND: VFXEffectConfig = {
  id: 'eviscerate_rend',
  name: 'Eviscerate Rend',
  category: 'burst',
  description: 'Two blood-red cuts cross over the target, one after the other, and droplets fall from the wound',
  primitives: [
    slashArc({ radius: 24, startDeg: -135, endDeg: -45, width: 6, color: BLOOD, durationMs: 220, glow: true }),
    slashArc({ radius: 24, startDeg: 45, endDeg: 135, width: 6, color: BLOOD, durationMs: 240, fadeDelayMs: 70 }),
    // The two cut lines (bright cores), drawn as thin diagonal quads.
    { type: 'polygon', polygonPoints: [-17, -15, -15, -17, 17, 15, 15, 17], color: 0xffd0d0, alpha: 0.9, depth: 97,
      tween: { alpha: 0, scale: 1.3, duration: 160, ease: 'Power2' } },
    { type: 'polygon', polygonPoints: [-17, 15, -15, 17, 17, -15, 15, -17], color: 0xffd0d0, alpha: 0.9, depth: 97,
      tween: { alpha: 0, scale: 1.3, duration: 180, ease: 'Power2', delay: 70 } },
    wispRise({ count: 5, spread: 14, radius: 2.5, color: BLOOD_DEEP, direction: 'down', travel: 22, durationMs: 420, delayMs: 90 }),
  ],
};

/** Assassinate's hit: a dark crimson ring closes on the point, then one killing flash. */
const LETHAL_STRIKE: VFXEffectConfig = {
  id: 'lethal_strike',
  name: 'Lethal Strike',
  category: 'burst',
  description: 'A deep crimson ring closes onto the target, a thin killing line runs through it and dark shards burst out',
  primitives: [
    ringBlast({ radius: 34, color: CRIMSON, alpha: 0.35, stroke: { color: CRIMSON_EDGE, width: 3 }, expandTo: 0.25, durationMs: 220, ease: 'Power2' }),
    { type: 'rect', width: 64, height: 2, color: 0xffffff, alpha: 0, depth: 98, glow: true,
      tween: { alpha: 1, scaleX: 1.4, duration: 120, ease: 'Power3', delay: 160 } },
    ringBlast({ radius: 10, color: CRIMSON_EDGE, alpha: 0.85, glow: true, expandTo: 3, durationMs: 300, delayMs: 200 }),
    shardBurst({ count: 6, spread: 26, color: CRIMSON, shard: { width: 9, height: 2 }, durationMs: 340, delayMs: 200, stroke: { color: CRIMSON_EDGE, width: 1 } }),
  ],
};

/** Cloak of Shadows' cast: night-violet smoke wraps in on the rogue. */
const SHADOW_CLOAK: VFXEffectConfig = {
  id: 'shadow_cloak',
  name: 'Shadow Cloak',
  category: 'burst',
  description: 'A night-violet ring of smoke closes in around the rogue and violet wisps rise as the cloak settles',
  primitives: [
    ringBlast({ radius: 54, color: NIGHT, alpha: 0.45, stroke: { color: NIGHT_EDGE, width: 3 }, expandTo: 0.45, durationMs: 340, ease: 'Sine.easeIn' }),
    { type: 'ellipse', width: 44, height: 64, color: NIGHT, alpha: 0.5, depth: 101,
      tween: { alpha: 0, scaleX: 1.25, duration: 520, ease: 'Sine.easeOut', delay: 200 } },
    wispRise({ count: 7, spread: 26, radius: 3, color: NIGHT_EDGE, travel: 40, durationMs: 620, delayMs: 160 }),
  ],
};

/** A Fan of Knives knife landing: a quick steel nick with a bead of poison. */
const KNIFE_NICK: VFXEffectConfig = {
  id: 'knife_nick',
  name: 'Knife Nick',
  category: 'burst',
  description: 'A quick steel nick across the target and a few beads of green poison',
  primitives: [
    slashArc({ radius: 14, startDeg: -60, endDeg: 20, width: 3, color: STEEL, durationMs: 160 }),
    { type: 'circle', radius: 3, color: POISON, alpha: 0.95, depth: 97,
      scatter: { count: 3, radius: 8 },
      tween: { y: 10, alpha: 0, duration: 360, ease: 'Sine.easeIn' } },
    ringBlast({ radius: 6, color: POISON, alpha: 0.5, expandTo: 2, durationMs: 220 }),
  ],
};

/** Vanish's cast: the rogue goes up in a burst of grey-violet smoke. */
const VANISH_POOF: VFXEffectConfig = {
  id: 'vanish_poof',
  name: 'Vanish Poof',
  category: 'burst',
  description: 'A burst of grey-violet smoke swallows the rogue, puffs drift up and a faint ring spreads at the feet',
  primitives: [
    groundCloud({ width: 90, height: 34, color: SMOKE, alpha: 0.65, offsetY: 8, swellTo: 1.6, durationMs: 700 }),
    { type: 'circle', radius: 16, color: SMOKE, alpha: 0.7, depth: 102,
      scatter: { count: 6, radius: 22 },
      tween: { scale: 2.2, alpha: 0, duration: 620, ease: 'Sine.easeOut' } },
    wispRise({ count: 6, spread: 24, radius: 5, color: SMOKE_DARK, travel: 46, durationMs: 700, delayMs: 80 }),
    ringBlast({ radius: 20, color: SMOKE, alpha: 0.3, stroke: { color: 0xc8c8e0, width: 2 }, expandTo: 2.8, durationMs: 500, delayMs: 60 }),
  ],
};

/** A Lucky Seven star landing: a gold five-point pop and glints. */
const LUCKY_STAR_BURST: VFXEffectConfig = {
  id: 'lucky_star_burst',
  name: 'Lucky Star Burst',
  category: 'burst',
  description: 'A gold five-point star pops on the target with bright glints — one of the seven lands',
  primitives: [
    starFlash({ points: 5, innerRadius: 5, outerRadius: 15, color: GOLD, glow: true, expandTo: 1.8, durationMs: 260 }),
    ringBlast({ radius: 8, color: GOLD_HOT, alpha: 0.6, expandTo: 2.4, durationMs: 240 }),
    glintScatter({ count: 4, spread: 18, color: GOLD_HOT, durationMs: 220, delayMs: 40 }),
  ],
};

/** Shadow Step's take-off: an indigo slit opens in the air and swallows the rogue. */
const SHADOW_TELEPORT: VFXEffectConfig = {
  id: 'shadow_teleport',
  name: 'Shadow Step Rift',
  category: 'burst',
  description: 'A tall indigo slit tears open where the rogue stood, narrows shut and leaves trailing shadow',
  primitives: [
    { type: 'rect', width: 26, height: 72, color: INDIGO, alpha: 0.75, depth: 101, glow: true,
      stroke: { color: INDIGO_EDGE, width: 2 },
      tween: { scaleX: 0.08, alpha: 0, duration: 300, ease: 'Quad.easeIn' } },
    { type: 'rect', width: 4, height: 84, color: INDIGO_EDGE, alpha: 0.95, depth: 102,
      tween: { scaleY: 0.2, alpha: 0, duration: 320, ease: 'Power2' } },
    wispRise({ count: 5, spread: 14, radius: 3, color: INDIGO, direction: 'down', travel: 24, durationMs: 420 }),
  ],
};

/** Shadow Step's arrival: the slit opens ahead and the rogue steps out of it. */
const SHADOW_STEP_EMERGE: VFXEffectConfig = {
  id: 'shadow_step_emerge',
  name: 'Shadow Step Emerge',
  category: 'burst',
  description: 'An indigo slit opens wide where the rogue lands, an indigo ring snaps out and shadow flecks scatter',
  primitives: [
    { type: 'rect', width: 4, height: 72, color: INDIGO_EDGE, alpha: 0.95, depth: 101, glow: true,
      tween: { scaleX: 7, alpha: 0, duration: 280, ease: 'Power2' } },
    ringBlast({ radius: 16, color: INDIGO, alpha: 0.55, stroke: { color: INDIGO_EDGE, width: 2 }, expandTo: 2.6, durationMs: 320, delayMs: 40 }),
    shardBurst({ count: 6, spread: 24, color: INDIGO, shard: { width: 6, height: 2 }, durationMs: 300, delayMs: 40 }),
  ],
};

export const ROGUE_KIT_VFX: VFXEffectConfig[] = [
  BACKSTAB_PUNCTURE,
  EVISCERATE_REND,
  LETHAL_STRIKE,
  SHADOW_CLOAK,
  KNIFE_NICK,
  VANISH_POOF,
  LUCKY_STAR_BURST,
  SHADOW_TELEPORT,
  SHADOW_STEP_EMERGE,
];
